import { Children, HTMLProps, ReactElement, useEffect, useState, useRef } from 'react';
import useFetchOptions from '../hooks/useFetchOptions';
import { NameValue } from '../types';
import Option, { OptionProps } from './Option';
import './style';

export interface SelectProps extends HTMLProps<HTMLDivElement> {
  onChange?: (value: any) => void;
  value?: string | number;
  defaultText?: string;
  disabled?: boolean;
  options?: NameValue[];
  maxListHeight?: string | number;
  getOptions?: () => Promise<NameValue[]>;
}

const Select = ({
  disabled,
  defaultText,
  value,
  onChange,
  children: propsChildren,
  options,
  getOptions,
  maxListHeight,
  ...rest
}: SelectProps) => {

  const [data] = useFetchOptions(getOptions, options ?? []);

  let children: ReactElement<OptionProps>[];
  if (propsChildren == null) {
    propsChildren = data.map((d, i) => <Option key={i} value={d.value}>{d.name}</Option>);
  }
  children = Children.toArray(propsChildren) as any[];

  const wrapperRef = useRef<HTMLDivElement>();
  const optionsRef = useRef<HTMLDivElement>();
  const valueRef = useRef<any>(undefined);

  const [isOpen, setOpen] = useState(false);
  const [, setValue] = useState(value);
  valueRef.current = value;

  const getHeight = () => isOpen ? (optionsRef.current.scrollHeight + 2) + 'px' : 0;

  const getOpenClass = () => isOpen ? ' is-open' : '';

  const getDisabledClass = () => disabled ? ' disabled' : '';

  const getText = () => {
    let search = valueRef.current;
    if (search === null) {
      if (value !== null) {
        search = value;
      }
      else if (children.length > 0) {
        search = children[0].props.value;
      }
    }
    const found = (children as any[]).find(ch => ch.props.value === search);
    if (found) {
      return found.props.children;
    }
    return defaultText ?? '';
  }

  const handleClick = (_event: React.MouseEvent) => {
    if (!disabled) {
      setOpen(!isOpen);
    }
  };

  const handleChange = (value: string) => {
    setOpen(false);
    valueRef.current = value;
    setValue(value);
    if (onChange) {
      onChange(value);
    }
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current) {
        const element = wrapperRef.current;
        if (!element.contains(event.target as Node)) {
          setOpen(false);
        }
      }
    }
    window.addEventListener('mousedown', handleOutsideClick, true);
    return () => {
      window.removeEventListener('mousedown', handleOutsideClick, true);
    }
  }, []);

  return (
    <div
      {...rest}
      tabIndex={0}
      className={'widget-select-cmp' + getOpenClass() + getDisabledClass()}
      onClick={handleClick} ref={wrapperRef}>
      <div className="widget-select-selector">
        <span>{getText()}</span>
        <span className="angle-wrapper">
          <i className={'far fa-angle-down' + (isOpen ? ' fa-rotate-180' : '')}></i>
        </span>
      </div>
      <div
        ref={optionsRef}
        className="widget-select-options"
        style={{ height: getHeight(), ...{ maxHeight: maxListHeight ?? undefined, overflowY: maxListHeight ? 'auto' : 'hidden' } }}>
        {children.map(child => (
          <div key={child.key}
            className="widget-option-item-wrapper"
            onClick={handleChange.bind(this, child.props.value)}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );

}

Select.Option = Option;

export default Select;
